"use client";

import React from "react";
import { User, Calendar, Tag, ArrowLeft, Loader, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";


declare const wvcClient: any;

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="228641" className="max-w-4xl mx-auto py-12 animate-pulse">
        <div className="h-10 bg-muted rounded-none w-3/4 mx-auto mb-6"></div>
        <div className="flex justify-center gap-4 mb-10">
          <div className="h-4 bg-muted rounded-none w-24"></div>
          <div className="h-4 bg-muted rounded-none w-24"></div>
        </div>
        <div className="h-[400px] bg-muted rounded-none mb-10"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-none w-full"></div>
          <div className="h-4 bg-muted rounded-none w-full"></div>
          <div className="h-4 bg-muted rounded-none w-5/6"></div>
          <div className="h-4 bg-muted rounded-none w-full"></div>
          <div className="h-4 bg-muted rounded-none w-4/5"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <CircleAlert className="w-12 h-12 text-destructive mx-auto mb-4" />
        <h2 className="text-2xl font-bold font-default mb-2">Failed to load publication</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-24 text-center">
        <h2 className="text-3xl font-bold font-default mb-4">Publication Not Found</h2>
        <p className="text-muted-foreground mb-8">The research paper or commentary you are looking for is currently unavailable.</p>
        <Link to="#">
          <Button className="rounded-none">Return to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="#" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Publications
        </Link>
      </div>

      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {post.categories.map((cat, i) => (
              <span key={cat.id} data-index={i} className="inline-flex items-center px-3 py-1 rounded-none bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest">
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default mb-6 leading-tight text-foreground" dangerouslySetInnerHTML={{ __html: post.title }} />
        
        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-sm font-medium">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8 border border-border rounded-none">
                <AvatarImage src={post.author.avatar?.['48']} alt={post.author.name} />
                <AvatarFallback className="rounded-none">{post.author.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <span className="font-default">{post.author.name}</span>
            </div>
          )}
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <time className="font-default" dateTime={post.date}>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-12 rounded-none overflow-hidden shadow-none border border-border">
          <AspectRatio ratio={16 / 9}>
            <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full" />
          </AspectRatio>
        </div>
      )}

      <div 
        className="prose-custom text-lg leading-relaxed text-foreground/90 font-default 
                   [&>p]:mb-6 
                   [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground
                   [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground
                   [&>h4]:text-xl [&>h4]:font-bold [&>h4]:font-default [&>h4]:mt-6 [&>h4]:mb-4 [&>h4]:text-foreground
                   [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2
                   [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2
                   [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:bg-muted [&>blockquote]:rounded-none
                   [&>img]:rounded-none [&>img]:my-8 [&>img]:shadow-none
                   [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80"
        dangerouslySetInnerHTML={{ __html: post.content || '' }} 
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap items-center gap-3">
          <Tag className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm font-bold font-default text-muted-foreground mr-2 uppercase tracking-wider">Keywords:</span>
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || '#'} data-index={i} className="px-3 py-1 bg-secondary hover:bg-primary hover:text-primary-foreground text-muted-foreground text-sm rounded-none transition-colors font-default">
              {tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {post.author && (
        <Card className="bg-secondary/50 border-border shadow-none rounded-none">
          <CardContent className="flex flex-col sm:flex-row items-center sm:items-start gap-6 pt-6">
            <Avatar className="w-20 h-20 border-2 border-background shadow-none rounded-none">
              <AvatarImage src={post.author.avatar?.['96']} alt={post.author.name} />
              <AvatarFallback className="text-2xl rounded-none">{post.author.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left flex-1">
              <h3 className="text-xl font-bold font-default mb-2 text-foreground">Analysis by {post.author.name}</h3>
              <p className="text-muted-foreground mb-4 font-default">
                Senior Fellow at Citizen Advocates Network (CAN). Specializing in data-driven governance and public policy research to empower responsive leadership.
              </p>
              <Link to={post.author.link || '#'}>
                <Button variant="outline" size="sm" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">View Research Profile</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-80 bg-muted rounded-none animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((p, i) => (
        <Card key={p.id} data-index={i} className="overflow-hidden flex flex-col h-full hover:border-primary transition-all duration-300 border-border bg-card group rounded-none shadow-none">
          {p.featuredImage && (
            <div className="overflow-hidden">
              <AspectRatio ratio={16 / 9}>
                <img 
                  src={p.featuredImage} 
                  alt={p.title} 
                  className="object-cover w-full h-full transform group-hover:scale-105 transition-transform duration-700" 
                />
              </AspectRatio>
            </div>
          )}
          <CardHeader className="flex-grow pt-6">
            {p.categories && p.categories.length > 0 && (
              <span className="text-xs font-bold text-primary uppercase tracking-widest mb-2 block">
                {p.categories[0].name}
              </span>
            )}
            <CardTitle className="line-clamp-2 text-xl font-bold font-default group-hover:text-primary transition-colors" dangerouslySetInnerHTML={{ __html: p.title }} />
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm line-clamp-3 font-default" dangerouslySetInnerHTML={{ __html: p.excerpt || '' }} />
          </CardContent>
          <CardFooter className="pt-0 pb-6">
            <Link to={p.link || '#'} className="inline-flex items-center text-primary font-bold text-sm hover:underline underline-offset-4 uppercase tracking-wider">
              Read Publication <ArrowLeft className="w-4 h-4 ml-1 rotate-180" />
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const mainQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedQuery = new WP_Query({ 
    post_type: currentWPQueryParams.post_type || 'post', 
    per_page: 3,
    
  });

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <PostsProvider wp_query={mainQuery}>
          <MainPostConsumer />
        </PostsProvider>

        {}
        <div className="max-w-5xl mx-auto mt-24">
          <div className="flex items-center justify-between mb-10 border-b border-border pb-4">
            <h2 className="text-3xl font-bold font-default text-foreground uppercase tracking-tight">Related Research</h2>
            <Link to="#">
              <Button variant="ghost" className="hidden sm:inline-flex rounded-none font-bold text-primary hover:bg-primary/10">Explore All Publications</Button>
            </Link>
          </div>
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>

      </div>
    </section>
  );
}